import React, { useState } from 'react';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import {
  CalendarBlank,
  SpinnerGap,
  Check
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { format, setHours, setMinutes } from 'date-fns';

const PlanVisitModal = ({ open, onOpenChange, restaurantId, restaurant, onSuccess }) => {
  const { token } = useAuth();
  const api = createApiClient(token);

  const [loading, setLoading] = useState(false);
  const [plannedDate, setPlannedDate] = useState(new Date());
  const [plannedTime, setPlannedTime] = useState('19:00');
  const [partySize, setPartySize] = useState('2');
  const [bookingLink, setBookingLink] = useState(restaurant?.booking_link || '');

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Combine date and time
      const [hours, minutes] = plannedTime.split(':').map(Number);
      const plannedDateTime = setMinutes(setHours(plannedDate, hours), minutes);

      const response = await api.post(`/restaurants/${restaurantId}/plan`, {
        planned_visit_at: plannedDateTime.toISOString(),
        booking_link: bookingLink.trim() || null,
        party_size: parseInt(partySize) || 2
      });

      onSuccess(response.data);
    } catch (error) {
      console.error('Error planning visit:', error);
      toast.error('Failed to plan visit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]" data-testid="plan-visit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Plan Visit</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Date */}
          <div>
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal mt-1.5"
                  data-testid="plan-date-btn"
                >
                  <CalendarBlank weight="duotone" className="w-4 h-4 mr-2" />
                  {format(plannedDate, 'PPP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={plannedDate}
                  onSelect={(date) => date && setPlannedDate(date)}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time */}
          <div>
            <Label htmlFor="time">Time</Label>
            <Input
              id="time"
              type="time"
              value={plannedTime}
              onChange={(e) => setPlannedTime(e.target.value)}
              className="mt-1.5"
              data-testid="plan-time-input"
            />
          </div>

          {/* Party Size */}
          <div>
            <Label htmlFor="party">Party Size</Label>
            <Input
              id="party"
              type="number"
              min="1"
              value={partySize}
              onChange={(e) => setPartySize(e.target.value)}
              className="mt-1.5"
              data-testid="plan-party-input"
            />
          </div>

          {/* Booking Link */}
          <div>
            <Label htmlFor="booking">Booking Link (optional)</Label>
            <Input
              id="booking"
              placeholder="https://..."
              value={bookingLink}
              onChange={(e) => setBookingLink(e.target.value)}
              className="mt-1.5"
              data-testid="plan-booking-input"
            />
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            className="w-full btn-primary"
            disabled={loading}
            data-testid="submit-plan-btn"
          >
            {loading ? (
              <SpinnerGap className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check weight="bold" className="w-5 h-5 mr-2" />
                Plan Visit
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanVisitModal;
